import React, { useCallback, useRef } from 'react';
import { FileSpreadsheet, Lock, UploadCloud } from 'lucide-react';
import Button from '../ui/Button';

interface FileDropzoneProps {
  onFileSelect: (file: File) => void;
}

const FileDropzone: React.FC<FileDropzoneProps> = ({ onFileSelect }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropzoneStyle: React.CSSProperties = {
    border: '1px dashed var(--border-strong)',
    borderRadius: '8px',
    padding: '4.25rem 2rem',
    textAlign: 'center',
    background: 'linear-gradient(145deg, rgba(255,255,255,0.055), rgba(255,255,255,0.014)), var(--bg-surface)',
    cursor: 'pointer',
    transition: 'border-color 0.2s, transform 0.2s, box-shadow 0.2s',
    boxShadow: 'var(--shadow-card)',
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFileSelect(e.dataTransfer.files[0]);
    }
  }, [onFileSelect]);

  return (
    <div
      id="upload-dropzone"
      style={dropzoneStyle}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      onMouseOver={(e) => {
        e.currentTarget.style.borderColor = 'var(--gabmic-orange)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 28px 70px rgba(0,0,0,0.42)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.borderColor = 'var(--border-strong)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-card)';
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: 'none' }}
        onChange={(event) => {
          const selectedFile = event.target.files?.[0];
          if (selectedFile) onFileSelect(selectedFile);
          event.target.value = '';
        }}
      />
      <span className="icon-tile" style={{ width: '58px', height: '58px', margin: '0 auto 1.35rem' }}>
        <FileSpreadsheet size={28} />
      </span>
      <h3 style={{ fontSize: '1.35rem', marginBottom: '0.45rem' }}>Drop your file here</h3>
      <p style={{ margin: '0 auto 1.5rem', fontSize: '0.95rem' }}>CSV or XLSX</p>
      <Button variant="secondary" type="button" onClick={(event) => {
        event.stopPropagation();
        inputRef.current?.click();
      }}>
        <UploadCloud size={17} />
        Choose file
      </Button>
      <p style={{ margin: '1.35rem auto 0', fontSize: '0.86rem', display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
        <Lock size={14} />
        Your data stays on this device for the first version.
      </p>
    </div>
  );
};

export default FileDropzone;
